import { useEffect, useRef } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/lib/auth-context";
import { bootstrapAccount } from "@/lib/onboarding.functions";

// Screens that are already part of the account/onboarding journey.
const EXEMPT = ["/auth", "/welcome", "/onboarding", "/reset-password"];

/**
 * Runs once per signed-in session, for every sign-in path (email or OAuth):
 * ensures the profile exists, sends the one-time welcome email server-side,
 * and routes brand-new members into the welcome experience.
 * Existing members are never interrupted.
 */
export function AccountBootstrap() {
  const { user, loading, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const run = useServerFn(bootstrapAccount);
  const done = useRef<string | null>(null);

  useEffect(() => {
    if (loading || !user || done.current === user.id) return;
    done.current = user.id;

    (async () => {
      try {
        const result = await run({ data: undefined as never });
        await refreshProfile();
        // onboardingStep > 0 means the member has already seen the welcome (or
        // started onboarding), so we never hijack their navigation again.
        if (!result.onboarded && result.onboardingStep === 0 && !EXEMPT.some((p) => pathname.startsWith(p))) {
          navigate({ to: "/welcome" });
        }
      } catch (e) {
        // Never block access if the bootstrap (or its email) fails.
        console.error("account bootstrap failed", e);
      }
    })();
  }, [user, loading, run, refreshProfile, navigate, pathname]);

  return null;
}
